import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { FiLayers, FiX } from 'react-icons/fi';
import { BASE_SCALAR_LAYER_IDS, FORECAST_LAYER_IDS, getEffectiveLayerConfig, type ForecastGridType } from '../../config/weatherLayers';
import { LAYER_GROUPS, EXTRA_LAYER_IDS, OBSERVATION_GROUP_IDS, type GroupDef, type VariantDef } from '../../config/layerGroups';
import { isLayerEnabled, getLayerSortOrder } from '../../config/remoteConfig';
import { useShallow } from 'zustand/react/shallow';
import { LayerKey, useWeatherStore } from '../../store/weatherStore';
import { isCompactViewport } from '../../store/helpers';
import { useModelLayers } from './hooks/useModelLayers';
import { useTranslation } from '../../i18n/LanguageContext';
import { useLayerLabels } from '../../i18n/useLayerLabels';

const isForecastLayer = (id: LayerKey): id is ForecastGridType =>
    (FORECAST_LAYER_IDS as string[]).includes(id);



/* ── تسخين كاش صور البطاقات ────────────────────────────────────────────────────
   اللوحة تُفكّ من الشجرة عند إغلاقها (يبقى الزرّ العائم وحده)، فكل فتحة كانت تُعيد
   إنشاء <img> من الصفر: طلب شبكة ثم فكّ ترميز ثم رسم — ومع `loading="lazy"` يضيف
   المتصفّح تأخيراً إضافياً قبل أن يبدأ الطلب أصلاً. النتيجة بطاقات تظهر فارغة ثم
   تمتلئ في كل مرة، رغم أن الصور ثابتة تماماً.
   الحلّ في طبقتين: أُسقط lazy (الصور 14 ملفاً بحجم ~10KB — لا شيء يُؤجَّل)، ونُسخّن
   كاش المتصفّح مرّة واحدة عند تحميل الوحدة. بعدها تأتي كل فتحة من الذاكرة فوراً. */
/* المصفوفة ليست زينة: بدونها تُجمَع عناصر Image فور خروجها من النطاق، ويُسقط
   المتصفّح معها الصورة من ذاكرته — فيعود الطلب عند كل فتح رغم "التسخين". قياساً:
   17 طلباً لثماني بطاقات قبل الاحتفاظ بالمراجع. المرجع الحيّ يُثبّت الصورة المفكوكة
   في الذاكرة فتُرسم لاحقاً بلا طلب شبكة إطلاقاً — لا حتى تحقّق 304، وهو ما يهمّ
   تحديداً لأن خادم التطوير يرسل no-cache على ملفّات public. */
const THUMB_CACHE: HTMLImageElement[] = [];

function warmThumbs(ids: LayerKey[]): void {
    if (THUMB_CACHE.length > 0 || typeof Image === 'undefined') return;
    for (const id of ids) {
        const img = new Image();
        img.decoding = 'async';
        img.src = `/layer-thumbs/${id}.webp`;
        THUMB_CACHE.push(img);
    }
}

// بلا props — memo يمنع إعادة الرسم مع كل تغيّر إطار زمني في NewWeatherMap.
export const LayerControls = memo(function LayerControls() {
    const {
        visibleLayers,
        setActiveLayer,
        closeAllLayers,
        layerControlsOpen,
        setLayerControlsOpen,
        selectedModel,
    } = useWeatherStore(useShallow((s) => ({ visibleLayers: s.visibleLayers, setActiveLayer: s.setActiveLayer, closeAllLayers: s.closeAllLayers, layerControlsOpen: s.layerControlsOpen, setLayerControlsOpen: s.setLayerControlsOpen, selectedModel: s.selectedModel })));
    const { t } = useTranslation();
    const { layerName, groupName, variantName } = useLayerLabels();

    /*
     * مرّة واحدة لكل جلسة — قائمة الطبقات ثابتة فلا يتغيّر ما يُسخَّن.
     *
     * ── لكن ليس أثناء إقلاع الخريطة ─────────────────────────────────────────────
     * كان التسخين يقع فور تركيب المكوّن، أي في نفس اللحظة التي يُطلب فيها أوّل راستر.
     * وقياس الموقع الحيّ (2026-09-02) أظهر اثنتي عشرة صورة مصغّرة تنطلق عند 2,118
     * مللي ثانية — نفس نبضة `meta.json` وأوّل إطار — وبعضها استغرق **1.8 ثانية**
     * لأنها تتقاسم فتحات الاتصال مع ما ينتظره الزائر فعلاً.
     *
     * وهي صور لا تُرى إلا بعد فتح قائمة الطبقات. فنؤجّلها إلى أوّل فراغ للمتصفّح
     * (‏requestIdleCallback) بعد ثانية من التركيب — فيبقى مكسب التسخين كاملاً (البطاقات
     * تُفتح ممتلئة من الذاكرة) بلا أن تزاحم الصورة الأولى.
     *
     * وإن فُتحت القائمة قبل ذلك فلا ضرر: الوسوم <img> في البطاقات تطلبها عندئذٍ، وهو
     * الوقت الذي تكون فيه أولويتها صحيحة أصلاً.
     */
    useEffect(() => {
        const ids = [...LAYER_GROUPS.map((g) => g.parentId), ...EXTRA_LAYER_IDS];
        const start = () => warmThumbs(ids);
        const ric = (window as unknown as {
            requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        }).requestIdleCallback;
        // Safari لا يدعم requestIdleCallback حتى اليوم — مهلة بسيطة بديلاً.
        const timer = window.setTimeout(
            () => { ric ? ric(start, { timeout: 4000 }) : start(); },
            1000,
        );
        return () => window.clearTimeout(timer);
    }, []);
    const panelRef = useRef<HTMLDivElement>(null);

    // إغلاق اللوحة عند الضغط خارجها — **على الموبايل فقط**: هناك تحتلّ اللوحة معظم الشاشة
    // فبقاؤها يحجب الخريطة، والضغط على الخريطة هو طريقة إغلاقها الطبيعية.
    // على الديسكتوب اللوحة شريط جانبي ضيّق لا يحجب شيئاً، وكانت كل نقرة على إحداثية تُغلقها
    // فيضطر المستخدم لإعادة فتحها عند كل تبديل طبقة — الآن لا تُغلق إلا من زر الإغلاق.
    // الفحص داخل المعالج لا في شرط الاشتراك كي يتبع تدوير الشاشة بلا إعادة ربط.
    useEffect(() => {
        if (!layerControlsOpen) return;
        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            if (!isCompactViewport()) return;
            const target = e.target as Node;
            if (panelRef.current && !panelRef.current.contains(target)) {
                setLayerControlsOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [layerControlsOpen, setLayerControlsOpen]);

    // الطبقات العاملة للنموذج المختار (من meta.json). نُخفي غير المتاحة من القائمة.
    const { status: modelStatus, layers: availableLayers } = useModelLayers(selectedModel);
    // كل طبقات التوقّع (بما فيها مؤشرا العواصف) تتبع meta.layers للنموذج المختار —
    // فتختفي تلقائياً من النماذج التي لا تولّد بياناتها (لا طبقة فارغة تطلب 404).
    const layerAvailable = (id: LayerKey): boolean =>
        isLayerEnabled(id)            // معطَّلة من الأدمن ⇒ تختفي من القائمة
        && (!isForecastLayer(id)
            || availableLayers.has(id));   // البلاطات/المتابعة دائماً متاحة

    const forecastAvailable = (id: ForecastGridType): boolean =>
        isLayerEnabled(id) && availableLayers.has(id);

    // ترتيب المجموعات والمتغيّرات حسب sort_order من إعدادات الأدمن (الافتراضي يطابق ترتيب الكود)
    const orderedGroups = [...LAYER_GROUPS]
        .map((g) => ({
            ...g,
            variants: g.variants
                ? [...g.variants].sort((a, b) => getLayerSortOrder(a.id) - getLayerSortOrder(b.id))
                : undefined,
        }))
        .sort((a, b) => {
            const aMin = a.variants ? Math.min(...a.variants.map((v) => getLayerSortOrder(v.id))) : getLayerSortOrder(a.parentId);
            const bMin = b.variants ? Math.min(...b.variants.map((v) => getLayerSortOrder(v.id))) : getLayerSortOrder(b.parentId);
            return aMin - bMin;
        });

    // عند تبديل النموذج: إن كانت طبقة التوقّع المعروضة غير متاحة في النموذج الجديد، ننتقل
    // لأوّل طبقة متاحة (منطق متّسق، بلا طبقة فارغة معطوبة). إن لم تتوفّر أيّ طبقة توقّع
    // (النموذج قيد التحضير) نُغلق طبقات التوقّع المعروضة فقط — ونتذكّر أننا من أغلقها.
    const closedByMissingModel = useRef(false);
    useEffect(() => {
        if (modelStatus === 'loading') return;
        const shownForecast = FORECAST_LAYER_IDS.filter((id) => visibleLayers[id]);
        const brokenShown = shownForecast.filter((id) => !forecastAvailable(id));

        if (modelStatus === 'missing') {
            if (brokenShown.length > 0) { closeAllLayers(); closedByMissingModel.current = true; }
            return;
        }

        if (brokenShown.length > 0) {
            // الرياح أوّلاً (الطبقة الافتراضية)، ثم أوّل طبقة متاحة إن لم يولّدها النموذج.
            const firstAvailable = forecastAvailable('wind')
                ? 'wind'
                : FORECAST_LAYER_IDS.find((id) => forecastAvailable(id));
            if (firstAvailable) setActiveLayer(firstAvailable);
            else closeAllLayers();
            closedByMissingModel.current = false;
            return;
        }

        // النموذج جاهز ولا طبقة توقّع ظاهرة — إن كنّا نحن من أطفأها (نموذج سابق غير متاح)
        // نُعيد الافتراضي (الرياح مثل Zoom Earth، أو أوّل طبقة متاحة) بدل بقاء الخريطة فارغة.
        if (shownForecast.length === 0 && closedByMissingModel.current) {
            closedByMissingModel.current = false;
            const preferred = forecastAvailable('wind')
                ? 'wind'
                : FORECAST_LAYER_IDS.find((id) => forecastAvailable(id));
            if (preferred) setActiveLayer(preferred);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelStatus, selectedModel]);

    /*
     * ── «دائماً خريطة»: لا تبقى الشاشة بلا طبقة أساس ─────────────────────────────
     *
     * بطلب المالك (2026-09-13): أي خلل صغير يُظهر الخريطة الافتراضية لا شاشةً فارغة.
     * طرق الوصول إلى «لا طبقة» كثيرة ومتفرّقة، فنحرس النتيجة لا كل طريق على حدة:
     * طبقة محفوظة في localStorage حُذفت من الكود، أو عطّلها الأدمن (applyRemoteModels
     * يطفئها)، أو إطفاء الرادار/القمر بالنقر عليهما ثانيةً، أو تفريغ الكاش.
     * الترتيب: الرياح (الافتراضية) ← أوّل طبقة يولّدها النموذج ← القمر الصناعي (لا
     * يعتمد على النموذج، فيبقى متاحاً حتى حين يكون النموذج «قيد التحضير»).
     */
    const noBaseShown = ![...BASE_SCALAR_LAYER_IDS, 'radar', 'satellite', ...EXTRA_LAYER_IDS]
        .some((id) => visibleLayers[id as LayerKey]);
    useEffect(() => {
        if (modelStatus === 'loading' || !noBaseShown) return;
        const fallback: LayerKey | undefined = forecastAvailable('wind')
            ? 'wind'
            : FORECAST_LAYER_IDS.find((id) => forecastAvailable(id))
                ?? (isLayerEnabled('satellite') ? 'satellite' : undefined);
        if (fallback) setActiveLayer(fallback);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelStatus, noBaseShown, availableLayers]);

    const groupActive = (g: GroupDef): boolean =>
        g.variants ? g.variants.some((v) => visibleLayers[v.id]) : !!visibleLayers[g.parentId];

    const activeCount = orderedGroups.filter(groupActive).length
        + EXTRA_LAYER_IDS.filter((id) => visibleLayers[id]).length;

    /* ⚠ يجب أن يبقى هذا قبل `if (!layerControlsOpen) return` أدناه: فيه useState، وأي خطّاف
       بعد إرجاعٍ مشروط يُغيّر عدد الخطافات بين عرضين فينهار التطبيق (React #310). */
    /* خيارات عرض الأعاصير انتقلت مع بطاقتها من لوحة التراكبات. المفتاح والحدث نفساهما
       (rasid:cyclone-display) فيقرؤها متتبّع الأعاصير كما كان، ويبقى تفضيل من ضبطها سابقاً. */
    type CycloneKey = 'past' | 'future' | 'disturbances';
    const [cycloneOptions, setCycloneOptions] = useState<Record<CycloneKey, boolean>>(() => {
        const dflt = { past: true, future: true, disturbances: true };
        try { return { ...dflt, ...JSON.parse(localStorage.getItem('rasid:cyclone-display') ?? '{}') }; }
        catch { return dflt; }
    });
    const toggleCyclone = (key: CycloneKey) => {
        let stored: Record<string, unknown> = {};
        try { stored = JSON.parse(localStorage.getItem('rasid:cyclone-display') ?? '{}'); } catch { /* تالف */ }
        const next = { ...cycloneOptions, [key]: !cycloneOptions[key] };
        const full = { systems: true, pointStyle: 'cyclone', ...stored, ...next };
        setCycloneOptions(next);
        try { localStorage.setItem('rasid:cyclone-display', JSON.stringify(full)); } catch { /* تخزين محجوب */ }
        window.dispatchEvent(new CustomEvent('rasid:cyclone-display', { detail: full }));
    };
    const CYCLONE_LABEL: Record<CycloneKey, 'cyclone.showPast' | 'cyclone.showFuture' | 'cyclone.showDisturbances'> = {
        past: 'cyclone.showPast', future: 'cyclone.showFuture', disturbances: 'cyclone.showDisturbances',
    };

    if (!layerControlsOpen) {
        /* الزرّ يقول **ما المعروض** لا «هنا طبقات» فقط — بطلب المالك (2026-09-13) على
           نمط Ventusky: اسم الطبقة بجانب الأيقونة، وتحته النموذج لطبقات التوقّع. من يرى
           ألواناً على الخريطة يعرف فوراً أنها «الأمطار · ICON» بلا فتح القائمة. */
        const shownId: LayerKey | undefined = orderedGroups
            .flatMap((g) => g.variants ? g.variants.map((v) => v.id) : [g.parentId])
            .concat(EXTRA_LAYER_IDS)
            .find((id) => visibleLayers[id]);
        return (
            <button
                className={`lsb-fab${shownId ? ' has-label' : ''} panel-open:max-[768px]:hidden`}
                onClick={() => setLayerControlsOpen(true)}
                title={t('layers.open')}
                aria-label={t('layers.open')}
            >
                {shownId && (
                    <span className="lsb-fab-text">
                        <span className="lsb-fab-name">{layerName(shownId)}</span>
                        {isForecastLayer(shownId) && (
                            <span className="lsb-fab-sub">{selectedModel.toUpperCase()}</span>
                        )}
                    </span>
                )}
                <span className="lsb-fab-icon">
                    <FiLayers />
                    {activeCount > 0 && <span className="lsb-fab-count">{activeCount}</span>}
                </span>
            </button>
        );
    }

    /**
     * المجموعات المعروضة فعلاً بعد استبعاد ما لا يولّده النموذج الحالي، ومع متغيّراتها
     * المتاحة.
     */
    const renderable: { g: GroupDef; variants?: VariantDef[] }[] = orderedGroups.flatMap((g) => {
        const variants = g.variants?.filter((v) => layerAvailable(v.id));
        if (g.variants) {
            return variants && variants.length > 0 ? [{ g, variants }] : [];
        }
        return layerAvailable(g.parentId) ? [{ g }] : [];
    });

    /* بطاقة طبقة: بلاطة ملوّنة بلون الطبقة + اسمها. النقر يفعّلها — والمجموعات ذات
       المتغيّرات تُفعّل أوّل متغيّر *متاح* للنموذج الحالي لا أوّل متغيّر مطلقاً. */
    const renderCard = (id: LayerKey, label: string, active: boolean, onClick: () => void) => {
        const cfg = getEffectiveLayerConfig(id);
        const Icon = cfg.icon;
        return (
            <button
                key={id}
                className={`lsb-card${active ? ' is-active' : ''}`}
                style={{ ['--tint' as string]: cfg.color }}
                onClick={onClick}
                aria-pressed={active}
            >
                <span className="lsb-thumb">
                    {/* الصورة رواستر الطبقة نفسه مصبوغاً بتدرّجها (راجع
                        scripts/build-layer-thumbs.mjs) — لا صورة زينة: البطاقة تُري
                        المستخدم ما سيراه على الخريطة فعلاً. onError يُسقط الصورة إن
                        كانت الطبقة بلا بلاطة مولَّدة، فيبقى التدرّج اللوني تحتها. */}
                    <img
                        className="lsb-thumb-img"
                        src={`/layer-thumbs/${id}.webp`}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    {/* الأعاصير بدوّامتها المفرغة لا بأيقونة من السجلّ: قيمة الأدمن المحفوظة
                        (override.icon) كانت تفرض صاعقة FiZap رغم تغيير الكود، والصاعقة توحي
                        بطبقة البرق. الدوّامة هي رمز الأعاصير في كل الواجهة (الزرّ والعلامات). */}
                    {id === 'hurricanes'
                        ? <span className="lsb-thumb-icon lsb-thumb-swirl" aria-hidden />
                        : <Icon className="lsb-thumb-icon" />}
                </span>
                <span className="lsb-label">{label}</span>
            </button>
        );
    };

    /* رقائق المتغيّرات تُعرض بعرض الشبكة كاملاً تحت البطاقة المفعّلة — لا داخلها:
       تمديد البطاقة كان يُشوّه الشبكة ويعيد توزيع ما بعدها على صفوف مختلطة. */
    const renderVariants = (variants: VariantDef[]) => (
        <div className="lsb-variants">
            {variants.map((v) => (
                <button
                    key={v.id}
                    className={`lsb-chip${visibleLayers[v.id] ? ' is-on' : ''}`}
                    onClick={() => setActiveLayer(v.id)}
                    aria-pressed={!!visibleLayers[v.id]}
                >
                    {variantName(v.id)}
                </button>
            ))}
        </div>
    );

    /* لا مفتاح للبرق هنا عمداً: هو جزءٌ من طبقات الأمطار والرادار والقمر الصناعي
       نفسها (كما في Windy)، يظهر معها دائماً — راجع NewWeatherMap حيث يُشتقّ من الطبقة. */

    const renderGroup = ({ g, variants }: { g: GroupDef; variants?: VariantDef[] }) => {
        const active = groupActive(g);
        return (
            <Fragment key={g.parentId}>
                {renderCard(g.parentId, groupName(g.parentId), active, () => {
                    if (variants && variants.length > 0) {
                        if (!active) setActiveLayer(variants[0].id);
                    } else {
                        setActiveLayer(g.parentId);
                    }
                })}
                {active && variants && variants.length > 1 && renderVariants(variants)}
            </Fragment>
        );
    };

    const observationGroups = renderable.filter(({ g }) => OBSERVATION_GROUP_IDS.includes(g.parentId));
    const weatherGroups = renderable.filter(({ g }) => !OBSERVATION_GROUP_IDS.includes(g.parentId));

    return (
        <aside ref={panelRef} className="lsb" aria-label={t('layers.title')}>
            <div className="lsb-head">
                <FiLayers className="text-[#9aa8ff]" />
                <span className="lsb-title">{t('layers.title')}</span>
                <button
                    className="lsb-close"
                    onClick={() => setLayerControlsOpen(false)}
                    title={t('common.close')}
                    aria-label={t('layers.close')}
                >
                    <FiX />
                </button>
            </div>

            <div className="lsb-body">
                {modelStatus === 'missing' && (
                    <div className="lsb-note">{t('layers.modelPending')}</div>
                )}

                {observationGroups.length > 0 && (
                    <section className="lsb-section">
                        <span className="lsb-section-title">{t('layers.sectionObservation')}</span>
                        <div className="lsb-grid">{observationGroups.map(renderGroup)}</div>
                    </section>
                )}

                {(weatherGroups.length > 0 || EXTRA_LAYER_IDS.some(layerAvailable)) && (
                    <section className="lsb-section">
                        <span className="lsb-section-title">{t('layers.sectionWeather')}</span>
                        <div className="lsb-grid">
                            {weatherGroups.map(renderGroup)}
                            {/* الغيوم وما شابهها ليست قسماً ثالثاً: هي عنصر طقسٍ كغيره،
                                وعزلها تحت «طبقات إضافية» كان يوحي بمرتبةٍ أدنى بلا سبب. */}
                            {EXTRA_LAYER_IDS.filter(layerAvailable).map((id) =>
                                renderCard(id, layerName(id), !!visibleLayers[id], () => setActiveLayer(id)))}
                            {/* الأعاصير بطاقةٌ كبقيّة الطبقات لا صفٌّ في لوحة التراكبات: هي ما
                                يبحث عنه المستخدم حين يسأل «أين العاصفة؟»، فمكانها بين عناصر الطقس. */}
                            {layerAvailable('hurricanes') && renderCard('hurricanes', layerName('hurricanes'),
                                !!visibleLayers.hurricanes, () => setActiveLayer('hurricanes'))}
                            {visibleLayers.hurricanes && (
                                <div className="lsb-variants">
                                    {(['past', 'future', 'disturbances'] as const).map((key) => (
                                        <button
                                            key={key}
                                            className={`lsb-chip${cycloneOptions[key] ? ' is-on' : ''}`}
                                            onClick={() => toggleCyclone(key)}
                                            aria-pressed={cycloneOptions[key]}
                                        >
                                            {t(CYCLONE_LABEL[key])}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                )}

            </div>
        </aside>
    );
});
